import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';

/**
 * Self-contained notification bell.
 * Calls useNotifications() internally — just drop <NotificationBell /> in the header.
 */
function NotificationBell() {
  const {
    notifications,
    unreadCount,
    permission,
    requestPermission,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const panelRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    if (!open) return;
    function handleOutsideClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  // Simple relative-time formatter (no external library needed)
  function formatTime(date) {
    if (!date) return '';
    try {
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d)) return '';
      const diffMs = Date.now() - d.getTime();
      if (diffMs < 60_000) return 'just now';
      if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
      if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
      if (diffMs < 604_800_000) return `${Math.floor(diffMs / 86_400_000)}d ago`;
      return d.toLocaleDateString();
    } catch {
      return '';
    }
  }

  const typeEmoji = {
    forum_post: '💬',
    forum_comment: '↩️',
    wall_comment: '📝',
    wall_note: '📌',
    bible_commentary: '📖',
    portion_commentary: '📜',
  };

  return (
    <div ref={panelRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* ── Bell button ── */}
      <button
        onClick={() => setOpen(prev => !prev)}
        title={
          unreadCount
            ? `${unreadCount} new notification${unreadCount > 1 ? 's' : ''}`
            : 'Notifications'
        }
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          fontSize: '22px',
          padding: '4px 6px',
          color: 'white',
          lineHeight: 1,
          verticalAlign: 'middle',
        }}
        aria-label="Notifications"
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              background: '#e53e3e',
              color: 'white',
              borderRadius: '50%',
              fontSize: '10px',
              fontWeight: 'bold',
              minWidth: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              fontFamily: 'sans-serif',
              lineHeight: 1,
              pointerEvents: 'none',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Drop-down panel ── */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: '310px',
            maxHeight: '420px',
            overflowY: 'auto',
            background: '#ffffff',
            border: '1px solid #dde3ed',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            zIndex: 3000,
            fontFamily: 'sans-serif',
          }}
        >
          {/* Panel header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px 10px',
              borderBottom: '1px solid #eef0f3',
              position: 'sticky',
              top: 0,
              background: '#ffffff',
              zIndex: 1,
            }}
          >
            <span style={{ fontWeight: 700, fontSize: '15px', color: '#1a1a2e' }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  fontSize: '12px',
                  background: 'none',
                  border: 'none',
                  color: '#0D3B8C',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Browser permission prompt */}
          {permission === 'default' && (
            <div
              style={{
                padding: '10px 16px',
                background: '#fffbeb',
                borderBottom: '1px solid #fde68a',
                fontSize: '12px',
                color: '#555',
              }}
            >
              <p style={{ margin: '0 0 6px' }}>
                Allow browser notifications to be alerted even when this tab is in the background.
              </p>
              <button
                onClick={requestPermission}
                style={{
                  background: '#0D3B8C',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Enable Notifications
              </button>
            </div>
          )}

          {/* Notification list */}
          {notifications.length === 0 ? (
            <div
              style={{
                padding: '28px 16px',
                textAlign: 'center',
                color: '#888',
                fontSize: '14px',
              }}
            >
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔔</div>
              No notifications yet
            </div>
          ) : (
            notifications.map(notif => (
              <div
                key={notif.id}
                onClick={() => {
                  markAsRead(notif.id);
                  if (notif.url) {
                    setOpen(false);
                    // In-app paths go through the router so they respect the
                    // site's base path (e.g. GitHub Pages) and don't reload.
                    if (/^https?:\/\//.test(notif.url)) window.location.href = notif.url;
                    else navigate(notif.url);
                  }
                }}
                style={{
                  display: 'flex',
                  gap: '10px',
                  padding: '11px 16px',
                  borderBottom: '1px solid #f3f4f6',
                  background: notif.read ? '#fff' : '#eef4ff',
                  cursor: notif.url ? 'pointer' : 'default',
                  transition: 'background 0.15s',
                  alignItems: 'flex-start',
                }}
              >
                {/* Type icon */}
                <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '2px' }}>
                  {typeEmoji[notif.type] || '🔔'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: '#333',
                      lineHeight: '1.4',
                      wordBreak: 'break-word',
                    }}
                  >
                    {notif.message}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#aaa' }}>
                    {formatTime(notif.created_at)}
                  </p>
                </div>
                {/* Unread dot */}
                {!notif.read && (
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#0D3B8C',
                      flexShrink: 0,
                      marginTop: '5px',
                    }}
                  />
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
